import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { fetchActiveCycle } from '../../services/reviewCycleService';
import { listInvitationsForCycle } from '../../services/reviewInvitationService';
import { getCycleDisplayLabel } from '../../services/cycleUtils';
import StatCard from '../../components/dashboard/StatCard';
import StatusBadge from '../../components/dashboard/StatusBadge';
import EmptyState from '../../components/ui/EmptyState';
import type { ReviewCycle, ReviewForm, ReviewWorkflowStatus } from '../../types';
import type { ReviewInvitation, ReviewResponse } from '../../types/magicLink';

interface FormWithAge extends ReviewForm {
  daysInStatus: number;
}

const STAGE_GROUPS: Array<{ key: string; label: string; statuses: ReviewWorkflowStatus[]; color: string }> = [
  { key: 'self',        label: 'Self',        statuses: ['prep_window', 'self_pending'],         color: 'bg-blue-500'    },
  { key: 'self_done',   label: '360° / Mgr',  statuses: ['self_submitted', '360_pending', '360_collected', 'manager_pending'], color: 'bg-purple-500' },
  { key: 'mgr_done',    label: 'Calibration', statuses: ['manager_submitted', 'calibration'],    color: 'bg-amber-500'   },
  { key: 'approved',    label: 'Sign-Off',    statuses: ['approved'],                            color: 'bg-emerald-500' },
  { key: 'done',        label: 'Done',        statuses: ['signed_off'],                          color: 'bg-emerald-700' },
];

const HRDashboard: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [cycle, setCycle] = useState<ReviewCycle | null>(null);
  const [forms, setForms] = useState<ReviewForm[]>([]);
  const [invitations, setInvitations] = useState<ReviewInvitation[]>([]);
  const [recentResponses, setRecentResponses] = useState<ReviewResponse[]>([]);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    void (async () => {
      try {
        const c = await fetchActiveCycle();
        if (!alive) return;
        if (!c) { setLoading(false); return; }
        setCycle(c);

        const [formsSnap, invs, recentSnap] = await Promise.all([
          getDocs(query(collection(db, 'reviewForms'), where('cycleId', '==', c.id))),
          listInvitationsForCycle(c.id),
          getDocs(query(
            collection(db, 'reviewResponses'),
            where('cycleId', '==', c.id),
            orderBy('submittedAt', 'desc'),
            limit(8),
          )).catch(() => null),
        ]);
        if (!alive) return;
        setForms(formsSnap.docs.map(d => ({ id: d.id, ...d.data() }) as ReviewForm));
        setInvitations(invs);
        if (recentSnap) setRecentResponses(recentSnap.docs.map(d => ({ id: d.id, ...d.data() }) as ReviewResponse));

        setLoading(false);
      } catch (e) {
        console.error('[HRDashboard] load failed:', e);
        setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [user]);

  // ─── Derived ─────────────────────────────────────────────────────────────
  const cycleLabel = cycle ? getCycleDisplayLabel(cycle.period, cycle.year) : '—';

  const lockDate = useMemo(() => {
    if (!cycle?.lockDate) return null;
    return (cycle.lockDate as { toDate?: () => Date }).toDate?.() ?? null;
  }, [cycle]);
  const daysToLock = lockDate ? Math.ceil((lockDate.getTime() - Date.now()) / 86400000) : null;

  // Funnel buckets
  const funnel = useMemo(() => {
    return STAGE_GROUPS.map(g => ({
      ...g,
      count: forms.filter(f => g.statuses.includes(f.status)).length,
    }));
  }, [forms]);

  const totalForms = forms.length;
  const completedCount = forms.filter(f => f.status === 'signed_off').length;
  const completionPct = totalForms ? Math.round((completedCount / totalForms) * 100) : 0;

  const submittedInvs = invitations.filter(i => i.status === 'submitted').length;
  const totalInvs = invitations.length;
  const invSubmissionPct = totalInvs ? Math.round((submittedInvs / totalInvs) * 100) : 0;

  // At-risk: forms stuck in early stages > 7 days OR invitations sent > 7 days unopened
  const atRisk = useMemo<FormWithAge[]>(() => {
    const now = Date.now();
    return forms
      .map(f => {
        const updatedAt = (f.updatedAt as { toDate?: () => Date } | undefined)?.toDate?.()
          ?? (f.createdAt as { toDate?: () => Date } | undefined)?.toDate?.();
        const daysInStatus = updatedAt ? Math.floor((now - updatedAt.getTime()) / 86400000) : 0;
        return { ...f, daysInStatus };
      })
      .filter(f => f.daysInStatus > 7 && !['approved', 'signed_off'].includes(f.status))
      .sort((a, b) => b.daysInStatus - a.daysInStatus)
      .slice(0, 5);
  }, [forms]);

  if (loading) return <div className="p-8 text-stone-500 text-sm">Loading HR dashboard…</div>;
  if (!cycle) return (
    <div className="max-w-3xl mx-auto p-8">
      <Greeting userName={user?.name} />
      <EmptyState
        icon="event_busy"
        title="No active cycle"
        description="Spin up the next review cycle to start dispatching reviews."
        action={
          <Link to="/cycles" className="inline-flex items-center gap-2 h-11 px-5 bg-stone-900 text-white text-sm font-medium rounded-xl hover:bg-stone-800">
            <span className="material-symbols-rounded" style={{ fontSize: 18 }} aria-hidden="true">add</span>
            Create cycle
          </Link>
        }
      />
    </div>
  );

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <div className="max-w-6xl mx-auto p-6 space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <Greeting userName={user?.name} cycleLabel={cycleLabel} />
        {daysToLock !== null && (
          <div className={`px-3 py-2 rounded-lg text-xs font-medium ${
            daysToLock <= 3 ? 'bg-red-50 text-red-700 border border-red-200' :
            daysToLock <= 7 ? 'bg-amber-50 text-amber-800 border border-amber-200' :
                              'bg-stone-50 text-stone-700 border border-stone-200'
          }`}>
            {daysToLock > 0
              ? `Cycle locks in ${daysToLock} day${daysToLock === 1 ? '' : 's'}`
              : daysToLock === 0
                ? 'Locks today'
                : `Locked ${Math.abs(daysToLock)} day${Math.abs(daysToLock) === 1 ? '' : 's'} ago`}
          </div>
        )}
      </div>

      {/* Top metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Reviewees"          value={totalForms}      sub="enrolled" />
        <StatCard label="Cycle completion"   value={`${completionPct}%`} sub={`${completedCount}/${totalForms} signed off`} tone={completionPct >= 80 ? 'ok' : completionPct >= 50 ? 'info' : 'neutral'} />
        <StatCard label="Magic links"        value={totalInvs}       sub={`${submittedInvs} submitted (${invSubmissionPct}%)`} tone={invSubmissionPct >= 80 ? 'ok' : 'info'} />
        <StatCard label="At-risk"            value={atRisk.length}   sub="stuck > 7 days" tone={atRisk.length > 0 ? 'risk' : 'ok'} />
      </div>

      {/* Quick actions */}
      <div className="bg-white border border-stone-200 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-stone-900 mb-3">Quick actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Link to="/reviews/new" className="flex items-center gap-3 p-4 border border-stone-200 rounded-lg hover:border-stone-500 hover:bg-stone-50 transition">
            <span className="material-symbols-rounded text-blue-600" style={{ fontSize: 22 }}>send</span>
            <div>
              <div className="text-sm font-medium text-stone-900">Start a review</div>
              <div className="text-[11px] text-stone-500">Magic-link dispatcher</div>
            </div>
          </Link>
          <Link to="/calibration" className="flex items-center gap-3 p-4 border border-stone-200 rounded-lg hover:border-stone-500 hover:bg-stone-50 transition">
            <span className="material-symbols-rounded text-amber-600" style={{ fontSize: 22 }}>balance</span>
            <div>
              <div className="text-sm font-medium text-stone-900">Open calibration</div>
              <div className="text-[11px] text-stone-500">{forms.filter(f => f.status === 'manager_submitted' || f.status === 'calibration').length} ready</div>
            </div>
          </Link>
          <Link to="/approval" className="flex items-center gap-3 p-4 border border-stone-200 rounded-lg hover:border-stone-500 hover:bg-stone-50 transition">
            <span className="material-symbols-rounded text-emerald-700" style={{ fontSize: 22 }}>task_alt</span>
            <div>
              <div className="text-sm font-medium text-stone-900">Sign-off queue</div>
              <div className="text-[11px] text-stone-500">{forms.filter(f => f.status === 'approved').length} awaiting sign-off</div>
            </div>
          </Link>
        </div>
      </div>

      {/* Cycle Funnel */}
      <div className="bg-white border border-stone-200 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-stone-900 mb-1">Cycle funnel</h2>
        <p className="text-[11px] text-stone-500 mb-4">Where reviewees are stuck — width is proportional to count.</p>
        <div className="flex items-stretch gap-1 h-16">
          {funnel.map((g, i) => {
            const widthPct = totalForms ? Math.max(g.count / totalForms * 100, 5) : 100 / funnel.length;
            return (
              <div
                key={g.key}
                className={`${g.color} rounded text-white text-xs flex flex-col items-center justify-center px-2 transition-all`}
                style={{ width: `${widthPct}%`, opacity: g.count === 0 ? 0.25 : 1 }}
                title={`${g.label}: ${g.count} reviewees`}
              >
                <div className="font-bold text-base tabular-nums">{g.count}</div>
                <div className="text-[10px] uppercase tracking-wider whitespace-nowrap">{g.label}</div>
                {i < funnel.length - 1 && (
                  <span className="absolute opacity-0 sr-only">→</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* At-Risk Reviews */}
      {atRisk.length > 0 && (
        <div className="bg-white border border-red-200 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-red-800 mb-1 flex items-center gap-1.5">
            <span className="material-symbols-rounded" style={{ fontSize: 18 }} aria-hidden="true">warning</span>
            At-risk reviews
          </h2>
          <p className="text-[11px] text-stone-500 mb-3">Stuck more than 7 days — may need a reminder or intervention.</p>
          <div className="space-y-2">
            {atRisk.map(f => (
              <div key={f.id} className="flex items-center justify-between p-3 rounded-lg bg-red-50 border border-red-100">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-stone-900">{f.revieweeName}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <StatusBadge status={f.status} />
                    <span className="text-[11px] text-red-700">stuck {f.daysInStatus} days</span>
                  </div>
                </div>
                <Link to={`/reviews/${f.id}`} className="text-xs text-blue-600 hover:underline shrink-0">Monitor →</Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent submissions */}
      {recentResponses.length > 0 && (
        <div className="bg-white border border-stone-200 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-stone-900 mb-3">Recent activity</h2>
          <div className="space-y-1.5">
            {recentResponses.map(r => {
              const submittedAt = (r.submittedAt as { toDate?: () => Date } | undefined)?.toDate?.();
              const ago = submittedAt ? minutesAgo(submittedAt) : '';
              const formMeta = forms.find(f => f.id === r.reviewFormId);
              const reviewerLabel = r.reviewerRole === 'self'
                ? `${formMeta?.revieweeName ?? '—'} (self)`
                : r.reviewerRole === 'line_manager'
                  ? `${r.reviewerEmail ?? 'Line manager'}`
                  : `Anonymous ${r.reviewerRole.replace('_', ' ')}`;
              return (
                <div key={r.id} className="flex items-center justify-between text-sm py-1.5 border-b border-stone-100 last:border-0">
                  <div className="min-w-0 flex-1">
                    <span className="text-stone-700">{reviewerLabel}</span>
                    <span className="text-stone-500"> submitted review for </span>
                    <span className="font-medium text-stone-900">{formMeta?.revieweeName ?? '—'}</span>
                  </div>
                  <span className="text-[11px] text-stone-400 shrink-0 ml-3">{ago}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Workflow distribution table was here — moved to /reports per audit #18 to reduce dashboard density */}
    </div>
  );
};

function minutesAgo(d: Date): string {
  const ms = Date.now() - d.getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1)   return 'just now';
  if (m < 60)  return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `${h}h ago`;
  const days = Math.floor(h / 24);
  return `${days}d ago`;
}

const Greeting: React.FC<{ userName?: string; cycleLabel?: string }> = ({ userName, cycleLabel }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wider text-stone-500">HR Dashboard</div>
    <h1 className="text-2xl font-semibold text-stone-900 tracking-tight">
      Hi, {userName?.split(' ')[0] ?? 'there'} 👋
    </h1>
    {cycleLabel && (
      <p className="text-sm text-stone-600 mt-1">Active cycle: <b>{cycleLabel}</b></p>
    )}
  </div>
);

export default HRDashboard;
